import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextStyle from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, ImagePlus, Smile, Palette, Braces, Smartphone, Undo2, Redo2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { TEMPLATE_VARIABLES } from "./TemplateVarsHint";
import { PhonePreview } from "./PhonePreview";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const COLORS = ["#0c4a6e", "#0284c7", "#0ea5e9", "#059669", "#f59e0b", "#dc2626", "#7c3aed", "#111827"];
const EMOJIS = "✈️ 🌴 🏖️ 🏝️ 🗺️ 🚢 🚆 🚌 🚗 🧳 🏨 🌎 ☀️ 🌊 ⛰️ 🏔️ 📸 🎒 🍹 🍽️ 🎉 🙌 😊 😍 🤩 👋 👉 ✅ ⚠️ ❗ 📄 📍 🕐 💳 🛂 💉 ❤️ ⭐ 🙏 📞".split(" ");

const toHtml = (v) => {
  if (!v) return "";
  if (/<(p|h[1-6]|ul|ol|li|br|strong|em|a|img|span|div)\b/i.test(v)) return v;
  const esc = (t) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return v.split(/\n{2,}/).filter((p) => p.trim()).map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("");
};

const Btn = ({ active, onClick, title, children, testId }) => (
  <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={testId}
    className={`h-8 min-w-8 px-1.5 rounded-md flex items-center justify-center text-sm transition-colors ${active ? "bg-sky-600 text-white" : "text-sky-800 hover:bg-sky-100"}`}>
    {children}
  </button>
);

const ImageButton = ({ editor, axiosConfig }) => {
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${BACKEND}/api/template-images`, fd, axiosConfig);
      editor.chain().focus().setImage({ src: `${BACKEND}${res.data.path}`, alt: file.name }).run();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" title="Imagen" className="h-8 w-8 rounded-md flex items-center justify-center text-sky-800 hover:bg-sky-100" data-testid="rte-image">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-2">
        <Button type="button" size="sm" className="w-full btn-sky text-white" onClick={() => fileRef.current?.click()} data-testid="rte-image-upload">Subir desde la compu</Button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="rte-image-file" />
        <div className="flex gap-1">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="o pegá un link https://…" className="h-8 text-xs" data-testid="rte-image-url" />
          <Button type="button" size="sm" variant="outline" className="h-8" data-testid="rte-image-url-insert"
            onClick={() => { if (url.trim()) { editor.chain().focus().setImage({ src: url.trim() }).run(); setUrl(""); } }}>OK</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const PickerButton = ({ title, icon, testId, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" title={title} className="h-8 w-8 rounded-md flex items-center justify-center text-sky-800 hover:bg-sky-100" data-testid={testId}>{icon}</button>
    </PopoverTrigger>
    <PopoverContent className="w-72 p-2">{children}</PopoverContent>
  </Popover>
);

export const RichTextEditor = ({ value, onChange, axiosConfig, previewKind, minHeight = 160, testId = "rich-editor" }) => {
  const [showPhone, setShowPhone] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false }),
      Image,
      TextStyle,
      Color,
    ],
    content: toHtml(value),
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? "" : ed.getHTML()),
  });

  useEffect(() => {
    if (editor && toHtml(value) !== editor.getHTML() && !(editor.isEmpty && !value)) {
      editor.commands.setContent(toHtml(value), false);
    }
  }, [value, editor]);

  if (!editor) return null;
  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link (https://… o una variable como {link_vouchers})", prev);
    if (url === null) return;
    if (!url) editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="rounded-xl border border-sky-200 bg-white overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-sky-100 bg-sky-50/60 px-1.5 py-1">
        <Btn title="Negrita" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} testId="rte-bold"><Bold className="w-4 h-4" /></Btn>
        <Btn title="Cursiva" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} testId="rte-italic"><Italic className="w-4 h-4" /></Btn>
        <Btn title="Título" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} testId="rte-h2"><Heading2 className="w-4 h-4" /></Btn>
        <Btn title="Subtítulo" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} testId="rte-h3"><Heading3 className="w-4 h-4" /></Btn>
        <PickerButton title="Color" icon={<Palette className="w-4 h-4" />} testId="rte-color">
          <div className="flex gap-1.5 flex-wrap">
            {COLORS.map((c) => (
              <button key={c} type="button" onClick={() => editor.chain().focus().setColor(c).run()} className="h-7 w-7 rounded-full border border-white shadow" style={{ background: c }} data-testid={`rte-color-${c.slice(1)}`} />
            ))}
            <button type="button" onClick={() => editor.chain().focus().unsetColor().run()} className="h-7 px-2 rounded-md text-xs border border-sky-200">Quitar</button>
          </div>
        </PickerButton>
        <Btn title="Lista" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} testId="rte-ul"><List className="w-4 h-4" /></Btn>
        <Btn title="Lista numerada" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} testId="rte-ol"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn title="Link" active={editor.isActive("link")} onClick={setLink} testId="rte-link"><Link2 className="w-4 h-4" /></Btn>
        <ImageButton editor={editor} axiosConfig={axiosConfig} />
        <PickerButton title="Emojis" icon={<Smile className="w-4 h-4" />} testId="rte-emoji">
          <div className="grid grid-cols-8 gap-1">
            {EMOJIS.map((e) => (
              <button key={e} type="button" onClick={() => editor.chain().focus().insertContent(e).run()} className="h-8 w-8 rounded hover:bg-sky-100 text-lg">{e}</button>
            ))}
          </div>
        </PickerButton>
        <PickerButton title="Insertar variable" icon={<Braces className="w-4 h-4" />} testId="rte-variables">
          <div className="space-y-0.5">
            {TEMPLATE_VARIABLES.map((v) => (
              <button key={v.key} type="button" onClick={() => editor.chain().focus().insertContent(v.key).run()} className="w-full text-left px-2 py-1 rounded hover:bg-sky-50" data-testid={`rte-var-${v.key.slice(1, -1)}`}>
                <code className="text-sky-700 text-xs">{v.key}</code> <span className="text-[11px] text-sky-600/70">{v.desc}</span>
              </button>
            ))}
          </div>
        </PickerButton>
        <Btn title="Deshacer" onClick={() => editor.chain().focus().undo().run()} testId="rte-undo"><Undo2 className="w-4 h-4" /></Btn>
        <Btn title="Rehacer" onClick={() => editor.chain().focus().redo().run()} testId="rte-redo"><Redo2 className="w-4 h-4" /></Btn>
        {previewKind && (
          <div className="ml-auto">
            <Btn title="Vista celular" active={showPhone} onClick={() => setShowPhone((s) => !s)} testId="rte-phone-toggle">
              <Smartphone className="w-4 h-4 mr-1" /> <span className="text-xs">Celular</span>
            </Btn>
          </div>
        )}
      </div>
      <div className={showPhone ? "grid md:grid-cols-[1fr_auto] gap-3 p-2" : ""}>
        <EditorContent editor={editor} className="rte-content px-3 py-2 text-sm text-sky-950" style={{ minHeight }} />
        {showPhone && <PhonePreview html={value} kind={previewKind} axiosConfig={axiosConfig} />}
      </div>
    </div>
  );
};
